/**
 * Total Appointments Per Staff Chart Component
 *
 * A reusable vertical bar chart component displaying data with year-over-year comparison.
 */

import React, { useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';

import styles from './TotalAppointmentsPerStaffChart.module.scss';
import { HorizontalBarChartData } from '../Common/HorizontalBarChart.tsx/HorizontalBarChart';
import { CustomTooltip } from '../Common/HorizontalBarChart.tsx/CustomTooltip';

interface TotalAppointmentsPerStaffChartProps {
    title: string;
    data: HorizontalBarChartData[];
}

// Chart colors
const CHART_COLORS = {
    current: '#000000',
    previous: '#E6E6E6'
} as const;

export const TotalAppointmentsPerStaffChart: React.FC<TotalAppointmentsPerStaffChartProps> = ({ title, data }) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ left: number } | null>(null);
    const chartRef = useRef<HTMLDivElement>(null);

    const handleBarHover = (data: any, index: number, event: any) => {
        setHoveredIndex(index);

        if (chartRef.current && event) {
            const chartRect = chartRef.current.getBoundingClientRect();
            const mouseX = event.clientX - chartRect.left;
            setTooltipPosition({ left: mouseX });
        }
    };

    const handleBarLeave = () => {
        setHoveredIndex(null);
        setTooltipPosition(null);
    };

    return (
        <div className={styles.chartContainer}>
            <div className={styles.chartHeader}>
                <h3 className={styles.chartTitle}>{title}</h3>
            </div>

            <div className={styles.chartContent}>
                <div
                    className={styles.chartArea}
                    ref={chartRef}
                >
                    {hoveredIndex !== null && tooltipPosition && (
                        <div
                            className={styles.tooltipWrapper}
                            style={{ left: `${tooltipPosition.left}px` }}
                        >
                            <CustomTooltip
                                active={true}
                                payload={[{ payload: data[hoveredIndex] }]}
                                showDots={true}
                            />
                        </div>
                    )}
                    <ResponsiveContainer
                        width="100%"
                        height={300}
                    >
                        <BarChart
                            data={data}
                            margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid
                                strokeDasharray="0"
                                stroke="#E8E8E8"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="name"
                                tick={false}
                                axisLine={false}
                                height={0}
                            />
                            <YAxis
                                tick={{ fill: '#666', fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                domain={[0, 140]}
                                ticks={[0, 20, 40, 60, 80, 100, 120, 140]}
                            />
                            {/* Current year bars (black, left) */}
                            <Bar
                                dataKey="currentValue"
                                radius={[4, 4, 0, 0]}
                                barSize={12}
                                fill={CHART_COLORS.current}
                                onMouseEnter={handleBarHover}
                                onMouseLeave={handleBarLeave}
                            />
                            {/* Previous year bars (gray, right) */}
                            <Bar
                                dataKey="previousValue"
                                radius={[4, 4, 0, 0]}
                                barSize={12}
                                fill={CHART_COLORS.previous}
                                onMouseEnter={handleBarHover}
                                onMouseLeave={handleBarLeave}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className={styles.labelsContainer}>
                    {data.map((item, index) => (
                        <div
                            key={index}
                            className={styles.barLabel}
                        >
                            {item.name}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
